import { useStore } from 'vuex'
import Misc from 'src/utils/helper/misc'
import { INGESTION_TAGS_LIMIT, INGESTION_TAG_CHAR_LIMIT } from 'src/utils/constants/common'
import { Toaster } from 'src/utils/helper/toaster'
import i18n from 'src/assets/i18n/i18n'

const { t } = i18n().global

// Aim of this composable is to provide utility functions that can be used in the submission process
// We will add small reusable functions here, that manipulate or helps to maintaining submission data
export function useSubmissionUtils() {
  const store = useStore();

  /**
   * The function `setSubmissionStatusInIngestion` updates the submission status in an inventory
   * ingestion system based on the provided status and submission data.
   * @param submissionData - Submission data object containing information about the submission,
   * including an `id` property.
   * @param status - The `status` parameter in the `setSubmissionStatusInIngestion` function represents
   * the status of a submission process. It can have two possible values: 'Success' or 'partial success'.
   * Depending on the status value, the function updates the submission status in the ingestion process
   * accordingly.
   */
  function setSubmissionStatusInIngestion(submissionData, status) {
    const submissionId = submissionData?.id
    if (status === 'Success' && submissionId) {
      store.commit('inventoryingestion/updateSubmissionStatus', { submissionId: submissionId, isComplete: true })
    } else if (status === 'partial success') {
      store.commit('inventoryingestion/updateSubmissionStatus', { submissionId: submissionId, isComplete: false })
    }
  }

  /**
   * The function `enforceExistingContractNumbers` takes input contract numbers, filters out existing
   * ones, and returns an updated list of contract numbers as a string.
   * @param params - Based on the provided function `enforceExistingContractNumbers`, it takes in a
   * `params` object as a parameter. The `params` object is expected to have the following structure:
   * @returns a string that contains the updated contract numbers, with any new contract numbers added to
   * the existing ones. The contract numbers are joined together with a comma and a space between each
   * number.
   */
  function enforceExistingContractNumbers(params) {
    const existingContractNumbers = params.data?.existingContractNumber ?
      params.data.existingContractNumber.split(',').map(cNum => cNum.trim()) :
      [];
    let inputNumbers = params?.newValue?.trim() ?
      params.newValue.trim()?.split(',').map(cNum => cNum.trim()) :
      [];
    const filteredInputNumbers = inputNumbers.filter(num => !existingContractNumbers.includes(num));
    const updatedContractNumbers = [...existingContractNumbers, ...filteredInputNumbers];
    return updatedContractNumbers.join(', ');
  }

  /**
   * The function `familyPriceEnabled` checks if certain conditions are met in the submission data and
   * ingestion type to determine if family pricing is enabled.
   * @param submissionData - The `submissionData` parameter is an object that contains information about
   * a user's submission. It includes a property `userMetaData` which in turn contains a property
   * `binaries` that is an array of binary data related to the submission.
   * @param ingestionType - The `ingestionType` parameter in the `familyPriceEnabled` function is used to
   * determine whether a certain condition is met based on the provided data. The function checks if the
   * condition `(multipleStyle || ttfvWithOtherFile) && ingestionType !== 'custom-fonts'` is true
   * @returns a boolean value based on the conditions specified. It will return `true` if either
   * `multipleStyle` is true or `ttfvWithOtherFile` is true, and `ingestionType` is not equal to
   * 'custom-fonts'. Otherwise, it will return `false`.
   */
  function familyPriceEnabled(submissionData, ingestionType) {
    let ttfvExists = false
    const activeSubmissionBinaries = submissionData?.userMetaData?.binaries || [];
    const uniqueStyles = new Set(activeSubmissionBinaries?.map(binary => {
      ttfvExists = ttfvExists || binary?.binariesData?.fontInfo?.formatFlavor === 'TTFV'
      // here giving priority to styleName, keeping fontInfo as fallback (using only fontInfo gives issues when same style nae file is moved)
      return binary?.styleName || binary?.binariesData?.fontInfo?.styleName
    }));
    const multipleStyle = uniqueStyles.size > 1
    const ttfvWithOtherFile = uniqueStyles.size === 1 && activeSubmissionBinaries.length > 1 && ttfvExists
    return (multipleStyle || ttfvWithOtherFile) && ingestionType !== 'custom-fonts';
  }

  /**
   * The function `getDesignersList` asynchronously fetches a list of designers based on a search value
   * and updates the designerOptions object with the retrieved data.
   * @param searchValue - The `searchValue` parameter is a string that is used to search for designers in
   * the `getDesignersList` function. It is checked to ensure its length is greater than 2 before
   * initiating the search for designers.
   * @param designerOptions - The `designerOptions` parameter in the `getDesignersList` function seems to
   * be an object that contains a `value` property. This `value` property is expected to have a `list`
   * array and a `loading` boolean property. The function first sets `designerOptions.value
   */
  async function getDesignersList(searchValue, designerOptions) {
    if (searchValue.length > 2) {
      designerOptions.value = {
        list: [],
        loading: true,
      };
      const response = await store.dispatch('designers/getDesigner', { name: searchValue })
      const list = response?.data?.collection?.map(designer => {
        return {
          label: designer.name,
          value: designer.name,
          designerId: designer.id
        }
      })
      designerOptions.value = {
        list,
        loading: false,
      };
    }
  }

  /**
   * The function `getContractDetailsRowData` asynchronously fetches contract details based on contract numbers
   * and returns formatted row data for the contract modal.
   * @param contractNumbersString - A comma-separated string of contract numbers to fetch details for.
   * @returns An array of contract detail objects with properties: contractNumber, vendor, contractType, isValid.
   * Returns an empty array if no contract details are found, or throws the error if the API call fails.
   */
  async function getContractDetailsRowData(contractNumbersString) {
    try {
      const contractDetails = await store.dispatch('inventoryingestion/getContractDetails', contractNumbersString);
      
      if (
        contractDetails &&
        Array.isArray(contractDetails.contractNumbersDetail)
      ) {
        return contractDetails.contractNumbersDetail.map(detail => ({
          contractNumber: detail.contractNumber,
          vendor: detail.vendor,
          contractType: detail.contractType,
          isValid: detail.isValid
        }));
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching contract details:', error);
      throw error;
    }
  }

  /**
   * Enhanced tag processing function that handles both single tags and comma-separated tags.
   * Performs comprehensive validation including character validation, banned word checks, and SEO validation.
   * @param {string} inputValue - The input string containing one or more tags (comma-separated or single)
   * @param {Array} existingTags - Array of existing tag objects with {id, name} structure
   * @param {Array} existingWarningWords - Array of existing warning words
   * @param {Object} route - Vue router route object for context
   * @param {string|number} activeSubmissionId - ID of the active submission
   * @param {number} existingTagLength - Number of existing tags
   * @param {string} componentName - Component name for banned word context (default: 'ingestion-tags')
   * @param {Object} storeInstance - Vuex store instance (optional, falls back to composable store)
   * @returns {Object} - Result object containing processed tags, errors, and warning words
   */
  function processTagsInput(inputValue, existingTags = [], existingWarningWords = [], route, activeSubmissionId, existingTagLength, componentName = 'ingestion-tags', storeInstance = null) {
    const result = {
      processedTags: [],
      warningWords: [...existingWarningWords],
      errors: {
        singleCharacterCheck: false,
        arrayOfInvalidCharacterTag: [],
        arrayOfBannedWordsTag: [],
        seoErrorsCheck: false,
        arrayOfMaxCharacterTag: [],
        maxTagCheck: false,
      },
      hasErrors: false
    };
    // Split by comma and process each tag
    const rawTags = inputValue.includes(',')
      ? inputValue.split(',').map(tag => tag.trim()).filter(tag => tag !== '')
      : [inputValue];

    const finalTagLength = rawTags.length + existingTagLength;
    if(finalTagLength > INGESTION_TAGS_LIMIT) {
      result.errors.maxTagCheck = true;
      result.hasErrors = true;
      return result;
    }

    for (const rawTag of rawTags) {
      if (rawTag === '') continue;

      // Single character check
      if (rawTag.length === 1) {
        result.errors.singleCharacterCheck = true;
        result.hasErrors = true;
        continue;
      }

      // Max character check
      if(rawTag.length > INGESTION_TAG_CHAR_LIMIT) {
        result.errors.arrayOfMaxCharacterTag.push(rawTag);
        result.hasErrors = true;
        continue;
      }

      // Invalid character check
      const invalidCharacterArray = Misc.handleInvalidTagValue(rawTag);
      if (invalidCharacterArray.length > 0) {
        result.errors.arrayOfInvalidCharacterTag.push(rawTag);
        result.hasErrors = true;
        continue;
      }

      // Check if tag already exists in the list (case-insensitive)
      const existingTag = existingTags.find(
        (tagItem) => tagItem.name.toLowerCase() === rawTag.toLowerCase()
      );
      if (existingTag) {
        Toaster.notifyError(
          t('pages.submissions.components.descriptions.tags.tag_already_exists', { tagName: existingTag.name })
        );
        continue;
      }

      if (result.processedTags.some((tagItem) =>
        tagItem.name.toLowerCase() === rawTag.toLowerCase()
      )) {
        continue;
      }
      

      // Banned word check
      const bannedWordInfo = Misc.strikeThroughBadWords(rawTag, storeInstance || store, componentName, route.name, activeSubmissionId);

      if (bannedWordInfo.bannedWordExist) {
        result.errors.arrayOfBannedWordsTag.push(rawTag);
        result.hasErrors = true;
        continue;
      }

      // Add to warning words if needed
      if (bannedWordInfo.warningWordExist) {
        if (!result.warningWords.includes(rawTag)) {
          result.warningWords.push(rawTag);
        }
      }

      // SEO validation
      const validatedTag = Misc.tagValidations(rawTag);
      if (validatedTag === false) {
        result.errors.seoErrorsCheck = true;
        result.hasErrors = true;
        continue;
      }

      const finalTag = {
        id: 0,
        name: Misc.capitalizeWord(validatedTag),
      };

      result.processedTags.push(finalTag);
    }
    return result;
  }

  /**
   * Re-aggregates AI tags from binary monotypeTags, filtering out user tags and excluded tags.
   * Pure function — no Vuex dependency. Reusable across move, map, and split flows.
   * Mirrors backend AiTagMergeTrait::mergeAiTagsFromBinaries algorithm.
   * @param {Object} params
   * @param {Array} params.binaries - Binary objects with monotypeTags arrays
   * @param {Array} params.existingTags - Current userMetaData.tags [{id, name}, ...]
   * @param {Array} params.excludedAiTags - Tag name strings the user has dismissed
   * @param {boolean} params.isLive - When true, skip tag cap (matching backend LIVE_TAGS_LIMIT)
   * @returns {Array} Merged tags array (user tags + recalculated AI tags)
   */
  function recalculateAiTags({ binaries, existingTags, excludedAiTags, isLive }) {
    const userTags = (existingTags || []).filter(tag => tag.id !== 'AI_GENERATED')

    const tagMap = new Map()
    ;(binaries || []).forEach(binary => {
      const tags = binary.monotypeTags || binary.binariesData?.monotypeTags || []
      tags.forEach(tag => {
        if (!tag?.name) return
        const key = tag.name.trim().toLowerCase()
        const confidence = tag.confidence || 0
        const existing = tagMap.get(key)
        if (!existing || confidence > existing.confidence) {
          tagMap.set(key, {
            name: tag.name.trim().charAt(0).toUpperCase() + tag.name.trim().slice(1),
            confidence
          })
        }
      })
    })

    const userTagNames = new Set(userTags.map(t => t.name.toLowerCase()))
    const excludedSet = new Set((excludedAiTags || []).map(t => t.toLowerCase().trim()))

    let candidates = Array.from(tagMap.values())
      .filter(t => !userTagNames.has(t.name.toLowerCase()) && !excludedSet.has(t.name.toLowerCase()))
      .sort((a, b) => b.confidence - a.confidence)

    if (!isLive) {
      const slots = Math.max(0, INGESTION_TAGS_LIMIT - userTags.length)
      candidates = candidates.slice(0, slots)
    }

    return [...userTags, ...candidates.map(t => ({ id: 'AI_GENERATED', name: t.name }))]
  }

  /**
   * Sorts tags array with warning word tags at the end
   * @param {Array} tags - Array of tag objects
   * @param {Array} warningWords - Array of warning words
   * @returns {Array} - Sorted tags array
   */
  function sortTagsWithWarnings(tags, warningWords) {
    return tags.sort((a, b) => {
      const aContainsWarning = warningWords.some(warningWord => 
        a.name.toLowerCase().includes(warningWord.toLowerCase())
      );
      const bContainsWarning = warningWords.some(warningWord => 
        b.name.toLowerCase().includes(warningWord.toLowerCase())
      );
      
      if (aContainsWarning === bContainsWarning) {
        return 0;
      } else if (aContainsWarning) {
        return 1;
      } else {
        return -1;
      }
    });
  }

  return {
    setSubmissionStatusInIngestion,
    enforceExistingContractNumbers,
    familyPriceEnabled,
    getDesignersList,
    getContractDetailsRowData,
    processTagsInput,
    sortTagsWithWarnings,
    recalculateAiTags
  }
}